import { Link } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useCookieConsent } from "@/hooks/useCookieConsent";
import { Mail, Heart, ExternalLink, Cookie, HelpCircle, PawPrint, Instagram, Facebook } from "lucide-react";
import { FAQModal } from "@/components/FAQModal";
import { Brand, BRAND_NAME } from "./Brand";

export const Footer = () => {
  const { isAdmin } = useAuth();
  const { resetConsent } = useCookieConsent();

  return (
    <footer className="bg-card border-t border-border">
      <div className="max-w-7xl mx-auto px-4 py-6 sm:py-8 sm:px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 lg:gap-12 lg:justify-items-center">
          {/* Brand */}
          <div className="space-y-2 sm:space-y-3">
            <Brand showIcon size="md" />
            <p className="text-[11px] sm:text-sm text-muted-foreground">
              Pomagamy łączyć ludzi ze schroniskami i organizacjami pomagającymi zwierzętom w całej Polsce.
            </p>
            <div className="flex flex-col gap-2">
              <FAQModal>
                <button className="flex w-fit items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary hover:text-primary/80 transition-colors">
                  <HelpCircle className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  Masz pytania? Zobacz FAQ
                </button>
              </FAQModal>
              <Link
                to="/dla-schronisk"
                className="flex w-fit items-center gap-1.5 text-[11px] sm:text-xs font-medium text-primary hover:text-primary/80 transition-colors"
              >
                <PawPrint className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                Broszura dla schronisk i fundacji
              </Link>
            </div>
            <div className="flex items-center gap-2 sm:gap-3 mt-2 sm:mt-3">
              <a 
                href="https://www.instagram.com/gdzieschronisko.pl"
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-4 h-4 text-primary" />
              </a>
              <a 
                href="https://www.facebook.com/gdzieschronisko"
                target="_blank" 
                rel="noopener noreferrer"
                className="w-8 h-8 sm:w-9 sm:h-9 rounded-full bg-primary/10 hover:bg-primary/20 flex items-center justify-center transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-4 h-4 text-primary" />
              </a>
            </div>
          </div>

          {/* Contact */}
          <div className="space-y-2 sm:space-y-3">
            <h3 className="font-semibold text-xs sm:text-sm">Kontakt</h3>
            <p className="text-[11px] sm:text-sm text-muted-foreground">
              Serwis {BRAND_NAME} jest prowadzony jako niezależny projekt informacyjny.
            </p>
            <ul className="space-y-1.5 sm:space-y-2 text-[11px] sm:text-sm text-muted-foreground">
              <li>
                <a 
                  href="mailto:kontakt@gdzieschronisko.pl" 
                  className="hover:text-foreground transition-colors flex items-center gap-1.5"
                >
                  <Mail className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
                  kontakt@gdzieschronisko.pl
                </a>
              </li>
            </ul>
            <p className="text-[10px] sm:text-xs text-muted-foreground pt-2 border-t border-border/50">
              Chcesz pomagać i aktualizować dane wybranego schroniska? Napisz do nas, aby zostać jego administratorem!
            </p>
          </div>

          {/* Resources - 2 columns on mobile, single on desktop */}
          <div className="space-y-2 sm:space-y-3">
            <h3 className="font-semibold text-xs sm:text-sm">Przydatne linki</h3>
            <ul className="grid grid-cols-2 sm:grid-cols-1 gap-x-4 gap-y-1.5 sm:gap-y-2 text-[11px] sm:text-sm text-muted-foreground">
              <li>
                <a 
                  href="https://napaluchu.waw.pl/edukacja/co-zrobic-jak-znajdziesz-zwierze/"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Znalazłeś zwierzę?
                  <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                </a>
              </li>
              <li>
                <a 
                  href="https://fundacjazwierz.org/jak-zglosic-znecanie-sie-nad-zwierzakiem/"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Zgłoś znęcanie
                  <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                </a>
              </li>
              <li>
                <a 
                  href="https://www.safe-animal.eu/pl/gdzie-zglosic-zaginiecie-psa" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Zaginiony pies/kot
                  <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                </a>
              </li>
              <li>
                <a 
                  href="https://pupilmed.pl/blog/pierwsza-pomoc-u-psa" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Pierwsza pomoc
                  <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                </a>
              </li>
              <li>
                <a 
                  href="https://www.lasy.gov.pl/pl/informacje/infografiki/co-zrobic-gdy-spotkasz-w-lesie" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Dzikie zwierzęta
                  <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                </a>
              </li>
              <li>
                <a 
                  href="https://toz.pl/jak-pomoc/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Jak pomagać? (TOZ)
                  <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                </a>
              </li>
              <li>
                <a 
                  href="https://www.wetgiw.gov.pl/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="hover:text-foreground transition-colors flex items-center gap-1"
                >
                  Inspekcja Weterynaryjna
                  <ExternalLink className="w-2.5 h-2.5 sm:w-3 sm:h-3 flex-shrink-0" />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar - compact on mobile */}
        <div className="mt-5 sm:mt-8 pt-4 sm:pt-6 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-2 sm:gap-4 text-[11px] sm:text-sm text-muted-foreground">
          <div className="flex items-center gap-2 sm:gap-4 flex-wrap justify-center sm:justify-start">
            <p>© 2025 {BRAND_NAME}</p>
            <Link to="/polityka-prywatnosci" className="hover:text-foreground transition-colors">
              Prywatność
            </Link>
            <button 
              onClick={resetConsent}
              className="hover:text-foreground transition-colors flex items-center gap-1"
            >
              <Cookie className="w-3 h-3 sm:w-3.5 sm:h-3.5" />
              Cookies
            </button>
          </div>
          <p className="flex items-center gap-1">
            Stworzone z <Heart className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-destructive fill-destructive" /> dla zwierząt
          </p>
        </div>
      </div>
    </footer>
  );
};
